import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Bookmark, CollectionsBookmark } from '@mui/icons-material'
import { BaseContentCard } from '../../general-card'
import { AddToPlaylistButton, BookmarkButton } from '../../action-buttons'
import { useAddToBookmarks } from '../../add-to-bookmarks'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import { useGroup } from '../../../hooks'
import { ALLOW_BOOKMARKS_GROUP, ALLOW_PLAYLISTS_GROUP } from '../../user-group-policy/permissions'

const useStyles = makeStyles({
  card: { maxWidth: '100% !important' }
})

/**
 * Renders a single MCQ quiz recommendation using the platform card layout,
 * matching the Bookmarks page style (horizontal, full-width on non-mobile).
 * Bookmark and add-to-playlist buttons are shown directly — no dropdown.
 *
 * @param {object} props.rec  Enriched recommendation from the backend (3512-B2)
 */
export const QuizRecommendationCard = ({ rec, onAddToPlaylist }) => {
  const navigate = useNavigate()
  const { isInGroup } = useGroup()
  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks({ from: 'quiz-result' })
  const displayAddToPlaylist = useDisplayAddToPlaylist()
  const classes = useStyles()

  const content = {
    locationId: rec.locationId || rec.sk,
    sk: rec.sk,
    staticUrl: rec.staticUrl,
    friendlyUrl: rec.friendlyUrl || rec.staticUrl,
    title: rec.title,
    subtitle: rec.description ? { html: rec.description } : undefined,
    primaryType: rec.type,
    image: rec.image ? { imgUrl: rec.image } : null
  }

  const onSelect = useCallback(() => {
    navigate(`${rec.url}?from=quiz-result`)
  }, [navigate, rec.url])

  if (!rec?.sk || !rec?.title || !rec?.type || !rec?.url || !rec?.staticUrl) return null

  const actions = {
    leftActions: (
      <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
        {isInGroup(ALLOW_BOOKMARKS_GROUP) && (
          <BookmarkButton
            size='xs'
            variant={isBookmarked(content) ? 'secondary' : 'secondary-noselect'}
            onClick={() => handleBookmark(content)}
            loading={bookmarking(content)}
            hideText
            startIcon={<Bookmark />}
            bookmarked={isBookmarked(content)}
            aria-label={isBookmarked(content) ? 'Remove from bookmarks' : 'Add to bookmarks'}
          />
        )}
        {isInGroup(ALLOW_PLAYLISTS_GROUP) && displayAddToPlaylist && (
          <AddToPlaylistButton
            size='xs'
            variant='secondary-noselect'
            startIcon={<CollectionsBookmark />}
            onClick={() => onAddToPlaylist(content)}
            aria-label='Add this item to a playlist'
          />
        )}
      </Box>
    )
  }

  return (
    <BaseContentCard
      content={content}
      onSelect={onSelect}
      actions={actions}
      className={classes.card}
      size='fullWidth'
      fullWidth
      right
      expand
    />
  )
}

export default QuizRecommendationCard
